import {
  CreateHL7TemplateRuleDto,
  HL7MessageGroup,
  QueryFactory,
  useEnabledDataBlockTypes,
  PatchHL7TemplateRuleDto,
  DataBlockVariant,
} from '@lmt/shared-types';
import {
  DataGrid,
  DXG,
  RowInsertingEvent,
  RowRemovingEvent,
  RowUpdatingEvent,
  useDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { AutoSizedTableWrapper } from '@lmt/unicorn-shared/app/components/common/AutoSizedTableWrapper';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Loading } from '../../../common/Loading';
import { visitInformationDataBlockTypeName } from '../../../measurements/common';
import {
  useHL7DocumentTypesList,
  useHL7QueryTypesList,
  useHL7MessageGroupsList,
  TemplateRuleInfo,
  getMessageTypeFromRuleInfo,
  getMessageTypeIdFromRuleDto,
} from './common';
import { useEventCallback } from '@mui/material';
import {
  allowOnlySingleNewRow,
  useCachedDataSourceWhenEditing,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/grid-helper';

type Props = {
  subtenantId?: number;
};

const nameof = nameofFactory<TemplateRuleInfo>();

export const HL7TemplateRulesTable: FC<Props> = (props) => {
  if (!props.subtenantId) {
    return null;
  }
  const modals = useModal();
  const i18n = useScopedTranslation('hl7.template_rules');
  const i18nextGlobal = useTranslation();
  useDataGrid();
  const dataGridRef = useRef<DataGrid<TemplateRuleInfo, string>>(null);
  const dataBlockTypes = useEnabledDataBlockTypes();
  const rulesQuery = QueryFactory.HL7TemplateRulesQuery.useGetQuery(
    {
      subtenantId: props.subtenantId,
    },
    {
      enabled: !!props.subtenantId,
    },
  );
  const templateRules: TemplateRuleInfo[] = useMemo(() => {
    return (rulesQuery.data ?? []).map((x) => {
      return {
        ...x,
        messageTypeId: getMessageTypeIdFromRuleDto(x),
      };
    });
  }, [rulesQuery.data]);
  const dataSource = useCachedDataSourceWhenEditing(dataGridRef, templateRules);

  const { data: templates } =
    QueryFactory.HL7OutgoingTemplatesQuery.useGetQuery();

  const HL7MessageGroups = useHL7MessageGroupsList();
  const hl7DocumentTypes = useHL7DocumentTypesList();
  const hL7QueryTypes = useHL7QueryTypesList();
  const dataBlockTypeOptions = useMemo(() => {
    return dataBlockTypes
      .filter(
        (type) =>
          !type.isDeactivated &&
          type.variant !== DataBlockVariant.Patient &&
          type.variant !== DataBlockVariant.SharedFields &&
          !(
            type.variant === DataBlockVariant.Visit &&
            type.value === visitInformationDataBlockTypeName
          ),
      )
      .map((x) => {
        return {
          value: x.id,
          displayValue: x.value,
          messageGroup: HL7MessageGroup.DataBlock,
        };
      });
  }, [dataBlockTypes]);

  const messageTypes = useMemo(() => {
    return [...dataBlockTypeOptions, ...hl7DocumentTypes, ...hL7QueryTypes].map(
      (option) => {
        return {
          ...option,
          value: option.value + '_' + option.messageGroup,
        };
      },
    );
  }, [hl7DocumentTypes, hL7QueryTypes, dataBlockTypeOptions]);

  const updateRules = useCallback(async () => {
    await rulesQuery.refetch();
  }, [props.subtenantId]);

  const messageTypesFilterFunc = useCallback(
    (options: any) => {
      return {
        store: messageTypes,
        filter: options.data
          ? [
              'messageGroup',
              '=',
              (options.data as TemplateRuleInfo).messageGroup,
            ]
          : null,
      };
    },
    [messageTypes],
  );

  const onMessageGroupChange = useCallback(
    (data: TemplateRuleInfo, value: HL7MessageGroup) => {
      data.messageTypeId = null;
      data.messageGroup = value;
    },
    [],
  );

  const onRowCreate = useEventCallback(
    (row: RowInsertingEvent<TemplateRuleInfo, string>) => {
      async function onRowCreate(data: TemplateRuleInfo) {
        const dto: CreateHL7TemplateRuleDto = {
          ...data,
          dataBlockTypeId: getMessageTypeFromRuleInfo(
            data,
            HL7MessageGroup.DataBlock,
          ),
          hL7DocumentType: getMessageTypeFromRuleInfo(
            data,
            HL7MessageGroup.Document,
          ),
          hL7QueryType: getMessageTypeFromRuleInfo(data, HL7MessageGroup.Query),
        };
        try {
          await QueryFactory.HL7TemplateRulesClient.create(dto);
          await updateRules();
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        }
        return false;
      }

      row.cancel = onRowCreate(row.data);
    },
  );

  const onRowEdit = useEventCallback(
    (row: RowUpdatingEvent<TemplateRuleInfo, string>) => {
      async function onRowEdit(
        oldData: TemplateRuleInfo,
        data: Partial<TemplateRuleInfo>,
      ) {
        const rule = {
          ...oldData,
          ...data,
        };
        const dto: PatchHL7TemplateRuleDto = {
          ...rule,
          dataBlockTypeId: getMessageTypeFromRuleInfo(
            rule,
            HL7MessageGroup.DataBlock,
          ),
          hL7DocumentType: getMessageTypeFromRuleInfo(
            rule,
            HL7MessageGroup.Document,
          ),
          hL7QueryType: getMessageTypeFromRuleInfo(rule, HL7MessageGroup.Query),
        };
        try {
          await QueryFactory.HL7TemplateRulesClient.patch(rule.id, dto);
          await updateRules();
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        }
        return false;
      }

      row.cancel = onRowEdit(row.oldData, row.newData);
    },
  );

  const onRowDelete = useEventCallback(
    (row: RowRemovingEvent<TemplateRuleInfo, string>) => {
      async function onRowDelete(row: TemplateRuleInfo) {
        const id = row.id;
        try {
          await QueryFactory.HL7TemplateRulesClient.remove(id);
          await updateRules();
        } catch (error) {
          await modals.showError({ text: parseError(error) });
          return true;
        }
        return false;
      }
      row.cancel = onRowDelete(row.data);
    },
  );

  return (
    <Loading loading={rulesQuery.isLoading} title={i18n.t('loading')}>
      <AutoSizedTableWrapper>
        <DXG.DataGrid
          ref={dataGridRef}
          dataSource={dataSource}
          keyExpr={'id'}
          showBorders={true}
          style={{ width: '100%' }}
          onInitNewRow={(e) => {
            const data = e.data;
            data.isActive = true;
            data.subtenantId = props.subtenantId!;
            data.id = 10000000 + Math.round(Math.random() * 10000);
            data.isAutoSending = false;
          }}
          onEditingStart={(e) => {
            if (e.data.isDefault) {
              if (e.column?.dataField === nameof('messageGroup')) {
                e.cancel = true;
              }
              if (e.column?.dataField === nameof('messageTypeId')) {
                e.cancel = true;
              }
              if (e.column?.dataField === nameof('documentTemplateId')) {
                e.cancel = true;
              }
            }
          }}
          onRowInserting={onRowCreate}
          onRowUpdating={onRowEdit}
          onRowRemoving={onRowDelete}
          onToolbarPreparing={allowOnlySingleNewRow}
        >
          <DXG.Paging enabled={false} />
          <DXG.Editing
            mode="batch"
            allowUpdating={true}
            allowAdding={true}
            allowDeleting={true}
            selectTextOnEditStart={true}
            startEditAction="click"
            newRowPosition="pageTop"
          />
          <DXG.Column dataField="id" sortOrder="desc" visible={false} />
          <DXG.Column
            dataField={nameof('messageGroup')}
            caption={i18n.t('columns.message_group')}
            setCellValue={onMessageGroupChange}
          >
            <DXG.Lookup
              dataSource={HL7MessageGroups as any}
              valueExpr="value"
              displayExpr="displayValue"
            />
            <DXG.RequiredRule />
          </DXG.Column>
          <DXG.Column
            dataField={nameof('messageTypeId')}
            caption={i18n.t('columns.message_type')}
          >
            <DXG.Lookup
              dataSource={messageTypesFilterFunc as any}
              valueExpr="value"
              displayExpr="displayValue"
            />
            <DXG.RequiredRule />
          </DXG.Column>
          <DXG.Column
            dataField={nameof('documentTemplateId')}
            caption={i18n.t('columns.template')}
            dataType="number"
          >
            <DXG.Lookup
              dataSource={(templates ?? [])
                .map((x) => ({
                  ...x,
                  title: x.isDefault
                    ? i18nextGlobal.t(x.title as any)
                    : x.title,
                }))
                .sort((a, b) => a.title.localeCompare(b.title))}
              displayExpr="title"
              valueExpr="id"
            />
            <DXG.RequiredRule />
          </DXG.Column>
          <DXG.Column
            width={230}
            dataField={nameof('serverAddress')}
            dataType="string"
            caption={i18n.t('columns.server_address')}
          />
          <DXG.Column
            width={170}
            dataField={nameof('serverPort')}
            dataType="number"
            caption={i18n.t('columns.server_port')}
            alignment="left"
          />
          <DXG.Column
            width={160}
            dataField={nameof('isAutoSending')}
            dataType="boolean"
            caption={i18n.t('columns.auto_sending')}
          />
          <DXG.Column
            width={120}
            dataField={nameof('isActive')}
            dataType="boolean"
            caption={i18n.t('columns.is_active')}
          />
        </DXG.DataGrid>
      </AutoSizedTableWrapper>
      <DXG.Column type="buttons">
        <DXG.Button name="delete" />
      </DXG.Column>
    </Loading>
  );
};
